import * as React from 'react';
import { FormState, HistoryEntry, Mode, Waypoint, AvoidPolygon, GlobalPolygon } from './types';
import { LS_URL, loadHistory, saveHistory, sliceResponseForHistory, loadGlobalPolygons, saveGlobalPolygons, MAX_GLOBAL_POLYGONS } from './lib/storage';
import { buildDirectionsBody, buildMatrixBody, endpointFor } from './lib/ors';
import { distanceToKm } from './lib/format';
import { geocodeCityName } from './lib/geocode';
import Waypoints from './components/Waypoints';
import DirectionsOptions from './components/DirectionsOptions';
import MatrixOptions from './components/MatrixOptions';
import TruckRestrictions from './components/TruckRestrictions';
import MapView from './components/MapView';
import RoutesPanel from './components/RoutesPanel';
import MatrixTable from './components/MatrixTable';
import JsonViewer from './components/JsonViewer';
import History from './components/History';
import LoadingOverlay from './components/LoadingOverlay';
import './styles.css';

const { useEffect, useMemo, useRef, useState } = React;

export const ORS_SERVERS = [
  { label: 'ORS 1 (181)', url: 'http://10.0.10.181:8080/ors' },
  { label: 'ORS 2 (182)', url: 'http://10.0.10.182:8080/ors' },
  { label: 'ORS 3 (183)', url: 'http://10.0.10.183:8080/ors' },
  { label: 'ORS 4 (184)', url: 'http://10.0.10.184:8080/ors' },
  { label: 'ORS 5 (185)', url: 'http://10.0.10.185:8080/ors' },
];

const DEFAULT_STATE: FormState = {
  baseUrl: 'http://10.0.10.184:8080/ors',
  profile: 'driving-hgv',
  mode: 'directions',
  waypoints: [
    { lon: 13.5779399, lat: 50.3602335 },
    { lon: 12.1711864, lat: 48.9816747 },
  ],
  units: 'km',
  preference: 'recommended',
  language: 'en',
  instructions: true,
  elevation: false,
  disableCH: false,
  radiuses: '',
  extras: { tollways: true },
  attributes: { avgspeed: true, detourfactor: true },
  avoid: {},
  restrictions: { length: '', width: '', height: '', weight: '', axleload: '', hazmat: false },
  distInfluence: '',
  priorityJson: '',
  speedJson: '',
  customModelOverride: false,
  customModelOverrideJson: '',
  cmFactors: [],
  altEnable: false,
  altCount: 2,
  altShare: 0.6,
  altWeight: 1.4,
  mSources: '',
  mDestinations: '',
  mResolve: false,
  mPreference: 'fastest',
  metrics: { distance: true, duration: true },
  cityMode: false,
  maximumSpeed: '',
  avoidPolygonsJson: '',
  avoidPolygons: [],
  fetchAllFormats: true,
};

export default function OrsPlaygroundApp() {
  const [form, setForm] = useState<FormState>(() => {
    const savedUrl = localStorage.getItem(LS_URL);
    return { ...DEFAULT_STATE, baseUrl: savedUrl || DEFAULT_STATE.baseUrl };
  });
  const set = (patch: Partial<FormState>) => setForm(prev => ({ ...prev, ...patch }));

  const [history, setHistory] = useState<HistoryEntry[]>(() => loadHistory());
  const [activeHistoryIdx, setActiveHistoryIdx] = useState<number | null>(null);
  const [globalPolygons, setGlobalPolygons] = useState<GlobalPolygon[]>(() => loadGlobalPolygons());

  const [sending, setSending] = useState(false);
  const [lastRequest, setLastRequest] = useState<any>(null);
  const [lastResponse, setLastResponse] = useState<any>(null);
  const [lastResponseGeoJson, setLastResponseGeoJson] = useState<any>(null);
  const [lastResponseGpx, setLastResponseGpx] = useState<string | null>(null);
  const [responseTab, setResponseTab] = useState<'json' | 'geojson' | 'gpx'>('json');
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [routesResponse, setRoutesResponse] = useState<any>(null);
  const [matrixResponse, setMatrixResponse] = useState<any>(null);
  const [matrixBody, setMatrixBody] = useState<any>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [pingResult, setPingResult] = useState<{ text: string; color?: string } | null>(null);
  const [pinging, setPinging] = useState(false);

  const [pickMode, setPickMode] = useState(false);
  const [fitBoundsSignal, setFitBoundsSignal] = useState(0);
  const [clearSignal, setClearSignal] = useState(0);

  // Polygon drawing/editing state
  const [drawPolygonMode, setDrawPolygonMode] = useState(false);
  const [editPolygonId, setEditPolygonId] = useState<string | null>(null);
  const [polygonVertices, setPolygonVertices] = useState<[number, number][]>([]);

  const onPolygonVertexAdd = (lon: number, lat: number) => {
    setPolygonVertices(prev => [...prev, [lon, lat]]);
  };
  const onPolygonVertexMove = (i: number, lon: number, lat: number) => {
    setPolygonVertices(prev => { const c = [...prev]; c[i] = [lon, lat]; return c; });
  };
  const onPolygonVertexRemove = (i: number) => {
    setPolygonVertices(prev => prev.filter((_, idx) => idx !== i));
  };
  const onPolygonVertexUndo = () => {
    setPolygonVertices(prev => prev.slice(0, -1));
  };
  const onPolygonFinish = () => {
    if (polygonVertices.length < 3) {
      alert('Need at least 3 points to form a polygon.');
      return;
    }
    if (editPolygonId) {
      // Update existing polygon
      set({ avoidPolygons: form.avoidPolygons.map(p =>
        p.id === editPolygonId ? { ...p, vertices: polygonVertices } : p
      )});
    } else {
      // Add new polygon
      const id = crypto.randomUUID();
      const label = `Polygon ${form.avoidPolygons.length + 1}`;
      set({ avoidPolygons: [...form.avoidPolygons, { id, label, vertices: polygonVertices, active: true }] });
    }
    setPolygonVertices([]);
    setDrawPolygonMode(false);
    setEditPolygonId(null);
  };
  const onPolygonCancel = () => {
    setPolygonVertices([]);
    setDrawPolygonMode(false);
    setEditPolygonId(null);
  };
  const onEditPolygon = (id: string) => {
    const p = form.avoidPolygons.find(p => p.id === id);
    if (!p) return;
    setEditPolygonId(id);
    setPolygonVertices(p.vertices.slice());
    setDrawPolygonMode(true);
  };
  const onDeletePolygon = (id: string) => {
    set({ avoidPolygons: form.avoidPolygons.filter(p => p.id !== id) });
    if (editPolygonId === id) { setPolygonVertices([]); setDrawPolygonMode(false); setEditPolygonId(null); }
  };
  const onTogglePolygonActive = (id: string) => {
    set({ avoidPolygons: form.avoidPolygons.map(p => p.id === id ? { ...p, active: !p.active } : p) });
  };
  const onToggleAllPolygons = (active: boolean) => {
    set({ avoidPolygons: form.avoidPolygons.map(p => ({ ...p, active })) });
  };
  const onClearAllPolygons = () => {
    set({ avoidPolygons: [], avoidPolygonsJson: '' });
    setPolygonVertices([]);
    setDrawPolygonMode(false);
    setEditPolygonId(null);
  };
  const onSavePolygonGlobal = (id: string) => {
    const p = form.avoidPolygons.find(p => p.id === id);
    if (!p) return;
    const current = loadGlobalPolygons();
    if (current.length >= MAX_GLOBAL_POLYGONS) {
      alert(`Global library is full (max ${MAX_GLOBAL_POLYGONS}). Delete one first.`);
      return;
    }
    const gp: GlobalPolygon = { id: crypto.randomUUID(), label: p.label, vertices: p.vertices, savedAt: Date.now() };
    const updated = [...current, gp];
    saveGlobalPolygons(updated);
    setGlobalPolygons(updated);
    flash(`"${p.label}" saved to global library`);
  };
  const onDeleteGlobalPolygon = (id: string) => {
    const updated = globalPolygons.filter(g => g.id !== id);
    saveGlobalPolygons(updated);
    setGlobalPolygons(updated);
  };
  const onAddGlobalToRoute = (gp: GlobalPolygon) => {
    const already = form.avoidPolygons.some(p => p.label === gp.label && p.vertices.length === gp.vertices.length);
    if (already && !confirm(`A polygon named "${gp.label}" is already in the route list. Add another copy?`)) return;
    set({ avoidPolygons: [...form.avoidPolygons, { id: crypto.randomUUID(), label: gp.label, vertices: gp.vertices, active: true }] });
  };

  // Alternate-routes UI state: which routes are visible on the map + which is selected.
  const [visibleRoutes, setVisibleRoutes] = useState<boolean[]>([]);
  const [selectedRouteIdx, setSelectedRouteIdx] = useState(0);

  // Reset visibility/selection whenever we get a new directions response.
  useEffect(() => {
    const n = routesResponse?.routes?.length ?? 0;
    setVisibleRoutes(Array.from({ length: n }, () => true));
    setSelectedRouteIdx(0);
  }, [routesResponse]);

  const toggleRouteVisible = (i: number) => {
    setVisibleRoutes(prev => {
      const copy = prev.slice();
      copy[i] = !copy[i];
      return copy;
    });
  };

  // Persist baseUrl on change.
  useEffect(() => {
    localStorage.setItem(LS_URL, form.baseUrl);
  }, [form.baseUrl]);

  const persistHistory = (h: HistoryEntry[]) => {
    saveHistory(h);
    setHistory(h.slice(0, 30));
  };

  const pushHistory = (entry: HistoryEntry) => {
    persistHistory([entry, ...history]);
    setActiveHistoryIdx(0);
  };

  // Update waypoints (also flashes fresh markers via MapView effect).
  const setWaypoints = (wps: Waypoint[]) => set({ waypoints: wps });
  const addWaypoint = (lon: number | null = null, lat: number | null = null) => {
    set({ waypoints: [...form.waypoints, { lon, lat }] });
  };

  // Automatically re-send after a marker drag ends. Uses ref to always call latest send().
  const sendRef = useRef<() => void>(() => {});

  const showRadiuses = form.mode === 'directions';

  async function send() {
    let body: any, mode: Mode = form.mode;

    // Resolve city names → coordinates when cityMode is active
    let resolvedForm = form;
    if (form.cityMode) {
      setSending(true);
      const resolved = await Promise.all(
        form.waypoints.map(async (wp) => {
          const name = wp.cityName?.trim();
          if (!name) return wp;
          // Skip if coordinates are already fresh
          if (wp.lon !== null && wp.lat !== null) return wp;
          const coords = await geocodeCityName(name);
          if (!coords) {
            throw new Error(`Could not geocode city: "${name}". Check the spelling or switch to coordinate mode.`);
          }
          return { ...wp, lon: coords[0], lat: coords[1] };
        })
      ).catch((e: any) => {
        setSending(false);
        alert(e.message);
        return null;
      });
      if (!resolved) return;
      // Update the waypoints with geocoded coordinates so the map shows them
      setForm(prev => ({ ...prev, waypoints: resolved }));
      resolvedForm = { ...form, waypoints: resolved };
    }

    try {
      if (mode === 'matrix') body = buildMatrixBody(resolvedForm);
      else body = buildDirectionsBody(resolvedForm);
    } catch (e: any) {
      alert(e.message);
      setSending(false);
      return;
    }

    const baseUrl = resolvedForm.baseUrl.replace(/\/$/, '');
    const jsonUrl = `${baseUrl}/v2/${endpointFor(mode, resolvedForm.profile, 'json')}`;

    setLastRequest(body);
    setLastResponse('Loading…');
    setLastResponseGeoJson(null);
    setLastResponseGpx(null);
    setResponseTab('json');
    setRoutesResponse(null);
    setMatrixResponse(null);
    setSending(true);
    const t0 = performance.now();

    // Fire extra format fetches in parallel (directions only)
    const fetchAllFormats = mode === 'directions' && resolvedForm.fetchAllFormats;
    const geoJsonFetch = fetchAllFormats
      ? fetch(`${baseUrl}/v2/${endpointFor(mode, resolvedForm.profile, 'geojson')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/geo+json' },
          body: JSON.stringify(body),
        }).then(r => r.json()).catch((e: any) => ({ error: e.message }))
      : Promise.resolve(null);
    const gpxFetch = fetchAllFormats
      ? fetch(`${baseUrl}/v2/${endpointFor(mode, resolvedForm.profile, 'gpx')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/gpx+xml' },
          body: JSON.stringify(body),
        }).then(r => r.text()).catch((e: any) => `<!-- Error: ${e.message} -->`)
      : Promise.resolve(null);

    let resJson: any, ok = false, errMsg: string | null = null;
    try {
      const res = await fetch(jsonUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });
      resJson = await res.json();
      ok = res.ok;
      if (!ok) errMsg = resJson?.error?.message || `HTTP ${res.status}`;
    } catch (e: any) {
      errMsg = e.message;
      resJson = { error: e.message };
    }
    const elapsed = Math.round(performance.now() - t0);
    setSending(false);
    setElapsedMs(elapsed);
    setLastResponse(resJson);

    // Settle extra format responses (they were already in-flight)
    Promise.all([geoJsonFetch, gpxFetch]).then(([gj, gpx]) => {
      if (gj !== null) setLastResponseGeoJson(gj);
      if (gpx !== null) setLastResponseGpx(gpx as string);
    });

    if (mode === 'matrix') {
      if (ok && (resJson.distances || resJson.durations)) {
        setMatrixResponse(resJson);
        setMatrixBody(body);
        pushHistory({
          ok, profile: form.profile, body, baseUrl: form.baseUrl, mode: 'matrix',
          summary: { ms: elapsed, n: body.locations.length },
          response: resJson, elapsed, at: Date.now(),
        });
      } else {
        setMatrixResponse(null);
        pushHistory({ ok: false, profile: form.profile, body, baseUrl: form.baseUrl, mode: 'matrix', error: errMsg!, at: Date.now() });
      }
    } else {
      if (ok && resJson.routes?.length) {
        setRoutesResponse(resJson);
        const units = resJson?.metadata?.query?.units || 'm';
        const s = resJson.routes[0].summary || {};
        pushHistory({
          ok, profile: form.profile, body, baseUrl: form.baseUrl, mode: 'directions',
          summary: {
            km: +distanceToKm(s.distance, units).toFixed(2),
            duration: s.duration,
            routes: resJson.routes.length,
            ms: elapsed,
          },
          response: sliceResponseForHistory(resJson),
          elapsed, at: Date.now(),
        });
      } else {
        setRoutesResponse(null);
        setClearSignal(x => x + 1);
        pushHistory({ ok: false, profile: form.profile, body, baseUrl: form.baseUrl, mode: 'directions', error: errMsg!, at: Date.now() });
      }
    }
  }
  sendRef.current = send;

  // ---- Handlers ----
  const onWaypointDragEnd = (i: number, lon: number, lat: number) => {
    const copy = form.waypoints.slice();
    copy[i] = { lon, lat };
    setForm(prev => ({ ...prev, waypoints: copy }));
    // Auto-send after drag if not currently sending.
    setTimeout(() => { if (!sending) sendRef.current(); }, 0);
  };

  const onPick = (lon: number, lat: number) => {
    addWaypoint(lon, lat);
    setPickMode(false);
  };

  const applyHistoryEntry = (e: HistoryEntry, opts: { restoreUrl: boolean }) => {
    const body: any = e.body || {};
    const isMatrix = e.mode === 'matrix' || !!body.locations;
    const coords: [number, number][] | undefined = body.coordinates || body.locations;
    const patch: Partial<FormState> = {
      mode: isMatrix ? 'matrix' : 'directions',
      profile: e.profile || form.profile,
      units: body.units || form.units,
    };
    if (opts.restoreUrl && e.baseUrl) patch.baseUrl = e.baseUrl;
    if (coords) patch.waypoints = coords.map(([lon, lat]) => ({ lon, lat }));

    const restrToState = (r: any) => ({
      length: r.length != null ? String(r.length) : '',
      width:  r.width  != null ? String(r.width)  : '',
      height: r.height != null ? String(r.height) : '',
      weight: r.weight != null ? String(r.weight) : '',
      axleload: r.axleload != null ? String(r.axleload) : '',
      hazmat: !!r.hazmat,
    });

    if (isMatrix) {
      patch.mSources = Array.isArray(body.sources) ? body.sources.join(',') : '';
      patch.mDestinations = Array.isArray(body.destinations) ? body.destinations.join(',') : '';
      patch.mResolve = !!body.resolve_locations;
      patch.metrics = Object.fromEntries(['distance', 'duration'].map(m =>
        [m, (body.metrics || ['distance', 'duration']).includes(m)])) as Record<string, boolean>;
      patch.restrictions = restrToState(body.profile_params?.restrictions || {});
    } else {
      patch.preference = body.preference || 'recommended';
      patch.language = body.language || 'en';
      patch.instructions = body.instructions !== false;
      patch.elevation = !!body.elevation;
      patch.radiuses = body.radiuses ? body.radiuses.join(',') : '';
      const extraKeys = ['tollways','waycategory','surface','waytype','steepness','suitability','roadaccessrestrictions','countryinfo','green','noise'];
      patch.extras = Object.fromEntries(extraKeys.map(k => [k, (body.extra_info || []).includes(k)]));
      const attrKeys = ['avgspeed', 'detourfactor', 'percentage'];
      patch.attributes = Object.fromEntries(attrKeys.map(k => [k, (body.attributes || []).includes(k)]));
      const avoidKeys = ['highways', 'tollways', 'ferries', 'fords', 'steps'];
      const av = body.options?.avoid_features || [];
      patch.avoid = Object.fromEntries(avoidKeys.map(k => [k, av.includes(k)]));
      // Restore maximum_speed + avoid_polygons
      patch.maximumSpeed = body.maximum_speed != null ? String(body.maximum_speed) : '';
      const ap = body.options?.avoid_polygons;
      if (ap?.type === 'MultiPolygon' && Array.isArray(ap.coordinates)) {
        patch.avoidPolygons = (ap.coordinates as number[][][][]).map((rings, i) => ({
          id: crypto.randomUUID(), label: `Polygon ${i + 1}`, active: true,
          vertices: (rings[0] || []).slice(0, -1).map(([lon, lat]: number[]) => [lon, lat] as [number, number]),
        }));
      } else if (ap?.type === 'Polygon' && Array.isArray(ap.coordinates)) {
        const ring = ap.coordinates[0] || [];
        patch.avoidPolygons = [{ id: crypto.randomUUID(), label: 'Polygon 1', active: true,
          vertices: ring.slice(0, -1).map(([lon, lat]: number[]) => [lon, lat] as [number, number]) }];
      } else {
        patch.avoidPolygons = [];
      }
      patch.avoidPolygonsJson = '';
      patch.restrictions = restrToState(body.options?.profile_params?.restrictions || {});
      const cm = body.custom_model || {};
      patch.distInfluence = cm.distance_influence != null ? String(cm.distance_influence) : '';
      // Restore cmFactors from custom_model priority/speed arrays
      const restoredFactors: typeof DEFAULT_STATE.cmFactors = [];
      if (Array.isArray(cm.priority)) {
        for (const rule of cm.priority) {
          if (rule.if && rule.multiply_by != null) {
            restoredFactors.push({
              id: crypto.randomUUID(),
              condition: rule.if,
              multiplyBy: String(rule.multiply_by),
              type: 'priority',
            });
          }
        }
      }
      if (Array.isArray(cm.speed)) {
        for (const rule of cm.speed) {
          if (rule.if && rule.limit_to != null) {
            restoredFactors.push({
              id: crypto.randomUUID(),
              condition: rule.if,
              multiplyBy: String(rule.limit_to),
              type: 'speed',
            });
          }
        }
      }
      patch.cmFactors = restoredFactors;
      patch.priorityJson = '';
      patch.speedJson = '';
      patch.customModelOverride = false;
      patch.customModelOverrideJson = Object.keys(cm).length ? JSON.stringify(cm, null, 2) : '';
      const alt = body.alternative_routes;
      patch.altEnable = !!alt;
      if (alt) {
        patch.altCount = alt.target_count ?? 2;
        patch.altShare = alt.share_factor ?? 0.6;
        patch.altWeight = alt.weight_factor ?? 1.4;
      }
    }
    setForm(prev => ({ ...prev, ...patch }));
  };

  const restoreResponse = (e: HistoryEntry) => {
    setLastRequest(e.body);
    if (e.ok && e.response) {
      setLastResponse(e.response);
      if (e.mode === 'matrix') {
        setMatrixResponse(e.response);
        setMatrixBody(e.body as any);
        setRoutesResponse(null);
      } else {
        setRoutesResponse(e.response);
        setMatrixResponse(null);
      }
      setElapsedMs(e.elapsed || 0);
    } else {
      setLastResponse(e.error ? `ERROR: ${e.error}` : '—');
      setRoutesResponse(null);
      setMatrixResponse(null);
      setClearSignal(x => x + 1);
    }
  };

  const onHistoryLoad = (e: HistoryEntry, idx: number) => {
    applyHistoryEntry(e, { restoreUrl: true });
    restoreResponse(e);
    setActiveHistoryIdx(idx);
  };
  const onHistoryReplayCurrent = (e: HistoryEntry, idx: number) => {
    applyHistoryEntry(e, { restoreUrl: false });
    setActiveHistoryIdx(idx);
    setTimeout(() => sendRef.current(), 0);
  };
  const onHistoryReplaySaved = (e: HistoryEntry, idx: number) => {
    applyHistoryEntry(e, { restoreUrl: true });
    setActiveHistoryIdx(idx);
    setTimeout(() => sendRef.current(), 0);
  };
  const onHistoryDelete = (idx: number) => {
    const h = history.slice();
    h.splice(idx, 1);
    persistHistory(h);
    if (activeHistoryIdx === idx) setActiveHistoryIdx(null);
  };
  const onHistoryClear = () => {
    if (!confirm('Clear request history?')) return;
    persistHistory([]);
    setActiveHistoryIdx(null);
  };

  const copyCurlForEntry = async (e: HistoryEntry) => {
    const base = e.baseUrl || form.baseUrl;
    const endpoint = endpointFor(e.mode, e.profile);
    const cmd = `curl.exe -X POST '${base.replace(/\/$/, '')}/v2/${endpoint}' -H 'Content-Type: application/json' -d '${JSON.stringify(e.body)}'`;
    await navigator.clipboard.writeText(cmd);
    flash('cURL copied');
  };

  const copyCurrentCurl = () => {
    try {
      const body = form.mode === 'matrix' ? buildMatrixBody(form) : buildDirectionsBody(form);
      const endpoint = endpointFor(form.mode, form.profile);
      const cmd = `curl.exe -X POST '${form.baseUrl.replace(/\/$/, '')}/v2/${endpoint}' -H 'Content-Type: application/json' -d '${JSON.stringify(body)}'`;
      navigator.clipboard.writeText(cmd);
      flash('Copied cURL to clipboard');
    } catch (e: any) { alert(e.message); }
  };

  const serverLabel = ORS_SERVERS.find(s => s.url === form.baseUrl)?.label ?? form.baseUrl;

  const buildCopyHeader = (format: string) => {
    const title = `// ${serverLabel} | ${form.profile} | ${format}`;
    const query = lastRequest != null
      ? `// Query: ${JSON.stringify(lastRequest)}`
      : '';
    return query ? `${title}\n${query}` : title;
  };

  const copyResponse = (tab: 'json' | 'geojson' | 'gpx') => {
    const data = tab === 'geojson' ? lastResponseGeoJson
      : tab === 'gpx' ? lastResponseGpx
      : lastResponse;
    if (data == null) { flash('No data yet'); return; }
    const body = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(`${buildCopyHeader(tab.toUpperCase())}\n\n${body}`);
    flash(`${tab.toUpperCase()} copied`);
  };

  const copyJsonAndGeoJson = () => {
    if (!lastResponse && !lastResponseGeoJson) { flash('No data yet'); return; }
    const jsonStr = lastResponse != null ? JSON.stringify(lastResponse, null, 2) : '(not available)';
    const geoStr = lastResponseGeoJson != null ? JSON.stringify(lastResponseGeoJson, null, 2) : '(not available)';
    const combined =
      `${buildCopyHeader('JSON + GeoJSON')}\n\n` +
      `// ===== JSON =====\n${jsonStr}\n\n` +
      `// ===== GeoJSON =====\n${geoStr}`;
    navigator.clipboard.writeText(combined);
    flash('JSON + GeoJSON copied');
  };

  const [flashMsg, setFlashMsg] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const flash = (msg: string) => {
    setFlashMsg(msg);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashMsg(null), 1500);
  };

  // Close copy context-menu on any click outside
  useEffect(() => {
    if (!copyMenuOpen) return;
    const handler = () => setCopyMenuOpen(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [copyMenuOpen]);

  const ping = async () => {
    setPinging(true);
    setPingResult({ text: 'pinging…' });
    try {
      const t0 = performance.now();
      const r = await fetch(`${form.baseUrl.replace(/\/$/, '')}/v2/health`);
      const j = await r.json();
      setPingResult({
        text: `${j.status || 'ok'} (${Math.round(performance.now() - t0)}ms)`,
        color: r.ok ? 'var(--ok)' : 'var(--err)',
      });
    } catch {
      setPingResult({ text: 'unreachable', color: 'var(--err)' });
    } finally {
      setPinging(false);
    }
  };

  const verb = form.mode === 'matrix' ? 'Computing matrix…' : 'Computing route…';

  const pingDisplay = useMemo(() => flashMsg || pingResult?.text || '', [flashMsg, pingResult]);
  const pingColor = flashMsg ? '' : (pingResult?.color || '');

  // --- Resizable sidebars ---
  const [leftWidth, setLeftWidth] = useState(340);
  const [rightWidth, setRightWidth] = useState(360);
  const dragging = useRef<'left' | 'right' | null>(null);
  const startX = useRef(0);
  const startW = useRef(0);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      if (dragging.current === 'left') {
        const newW = Math.max(220, Math.min(600, startW.current + (e.clientX - startX.current)));
        setLeftWidth(newW);
      } else {
        const newW = Math.max(220, Math.min(700, startW.current - (e.clientX - startX.current)));
        setRightWidth(newW);
      }
    };
    const onUp = () => { dragging.current = null; document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  const startResize = (side: 'left' | 'right', e: React.MouseEvent) => {
    dragging.current = side;
    startX.current = e.clientX;
    startW.current = side === 'left' ? leftWidth : rightWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div className="ors-playground-root">
      <header className="topbar">
        <h1>🚛 ORS Playground (React)</h1>
        <div className="server">
          <label>ORS Server</label>
          <select
            value={ORS_SERVERS.find(s => s.url === form.baseUrl) ? form.baseUrl : '__custom__'}
            onChange={(e) => {
              if (e.target.value !== '__custom__') set({ baseUrl: e.target.value });
            }}
            style={{ maxWidth: 180 }}
          >
            {ORS_SERVERS.map(s => <option key={s.url} value={s.url}>{s.label}</option>)}
            {!ORS_SERVERS.find(s => s.url === form.baseUrl) && (
              <option value="__custom__">Custom</option>
            )}
          </select>
          <input
            type="text" placeholder="http://10.0.10.184:8080/ors"
            value={form.baseUrl}
            onChange={(e) => set({ baseUrl: e.target.value })}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <button onClick={ping} disabled={pinging} title="Test server health">Ping</button>
          <span className="muted" style={{ color: pingColor }}>{pingDisplay}</span>
        </div>
        <div className="header-actions">
          <button
            className={`primary${sending ? ' loading' : ''}`}
            onClick={() => send()}
            disabled={sending}
          >
            {sending ? 'Sending…' : '▶ Send Request'}
          </button>
          <button onClick={copyCurrentCurl}>📋 cURL</button>
          <button onClick={() => { setClearSignal(x => x + 1); setRoutesResponse(null); setMatrixResponse(null); }}>
            🗑 Clear
          </button>
        </div>
      </header>

      <main className="layout" style={{ '--left-w': `${leftWidth}px`, '--right-w': `${rightWidth}px` } as React.CSSProperties}>
        {/* LEFT: form */}
        <aside className="panel form-panel">
          <div className="tabs">
            <button
              className={`tab-btn${form.mode === 'directions' ? ' active' : ''}`}
              onClick={() => set({ mode: 'directions' })}
            >📍 Directions</button>
            <button
              className={`tab-btn${form.mode === 'matrix' ? ' active' : ''}`}
              onClick={() => set({ mode: 'matrix' })}
            >📊 Matrix</button>
          </div>

          <section>
            <h2>Profile</h2>
            <select
              value={form.profile}
              onChange={(e) => set({ profile: e.target.value })}
            >
              <option value="driving-hgv">driving-hgv (LKW)</option>
              <option value="driving-car">driving-car (PKW)</option>
              <option value="cycling-regular">cycling-regular</option>
              <option value="foot-walking">foot-walking</option>
            </select>
          </section>

          <section>
            <h2>
              {form.mode === 'matrix' ? 'Locations' : 'Waypoints'}
              {' '}
              <small style={{ fontWeight: 'normal', fontSize: '0.75em' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.cityMode}
                    onChange={(e) => {
                      set({ cityMode: e.target.checked });
                      if (e.target.checked) {
                        // Preserve any existing city names; clear coordinates so they re-geocode
                        setForm(prev => ({
                          ...prev,
                          cityMode: true,
                          waypoints: prev.waypoints.map(wp => ({
                            ...wp,
                            cityName: wp.cityName ?? '',
                            lon: null,
                            lat: null,
                          })),
                        }));
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  🏙 City names
                </label>
                {!form.cityMode && <span style={{ marginLeft: 6, color: 'var(--muted)' }}>(lon, lat)</span>}
              </small>
            </h2>
            <Waypoints
              waypoints={form.waypoints}
              onChange={setWaypoints}
              onAdd={() => addWaypoint()}
              onPickToggle={() => setPickMode(v => !v)}
              pickMode={pickMode}
              showRadiuses={showRadiuses}
              radiuses={form.radiuses}
              onRadiusesChange={(v) => set({ radiuses: v })}
              cityMode={form.cityMode}
            />
          </section>

          {form.mode === 'matrix' && <MatrixOptions form={form} set={set} />}
          {form.mode === 'directions' && (
            <DirectionsOptions
              form={form}
              set={set}
              drawPolygonMode={drawPolygonMode}
              editingPolygonId={editPolygonId}
              onStartDrawPolygon={() => { setDrawPolygonMode(true); setPolygonVertices([]); setEditPolygonId(null); }}
              onEditPolygon={onEditPolygon}
              onDeletePolygon={onDeletePolygon}
              onTogglePolygonActive={onTogglePolygonActive}
              onToggleAllPolygons={onToggleAllPolygons}
              onClearAllPolygons={onClearAllPolygons}
              onSavePolygonGlobal={onSavePolygonGlobal}
              globalPolygons={globalPolygons}
              onDeleteGlobalPolygon={onDeleteGlobalPolygon}
              onAddGlobalToRoute={onAddGlobalToRoute}
            />
          )}

          {form.profile === 'driving-hgv' && <TruckRestrictions form={form} set={set} />}

          <section>
            <h2>Other</h2>
            <div className="grid2">
              <label>units
                <select value={form.units} onChange={(e) => set({ units: e.target.value })}>
                  <option value="km">km</option>
                  <option value="m">m</option>
                  <option value="mi">mi</option>
                </select>
              </label>
              {form.mode === 'directions' && (
                <label title="Also send GeoJSON and GPX requests in parallel so all formats are available in the response panel">
                  GeoJSON &amp; GPX
                  <input
                    type="checkbox"
                    checked={form.fetchAllFormats}
                    onChange={(e) => set({ fetchAllFormats: e.target.checked })}
                  />
                </label>
              )}
              {form.mode === 'directions' && (
                <label>language
                  <select value={form.language} onChange={(e) => set({ language: e.target.value })}>
                    <option value="en">en</option>
                    <option value="de">de</option>
                    <option value="fr">fr</option>
                    <option value="es">es</option>
                  </select>
                </label>
              )}
              {form.mode === 'directions' && (
                <label>instructions
                  <input type="checkbox" checked={form.instructions}
                    onChange={(e) => set({ instructions: e.target.checked })} />
                </label>
              )}
              {form.mode === 'directions' && (
                <label>elevation
                  <input type="checkbox" checked={form.elevation}
                    onChange={(e) => set({ elevation: e.target.checked })} />
                </label>
              )}
            </div>
          </section>
        </aside>

        {/* LEFT resize handle */}
        <div className="resize-handle" onMouseDown={(e) => startResize('left', e)} />

        {/* CENTER: map + stats */}
        <section className="panel map-panel">
          <MapView
            waypoints={form.waypoints}
            onWaypointDragEnd={onWaypointDragEnd}
            pickMode={pickMode}
            onPick={onPick}
            routesResponse={routesResponse}
            matrixResponse={matrixResponse}
            fitBoundsSignal={fitBoundsSignal}
            clearSignal={clearSignal}
            visibleRoutes={visibleRoutes}
            selectedRouteIdx={selectedRouteIdx}
            onRouteClick={setSelectedRouteIdx}
            drawPolygonMode={drawPolygonMode}
            editingPolygonId={editPolygonId}
            polygonVertices={polygonVertices}
            onPolygonVertexAdd={onPolygonVertexAdd}
            onPolygonVertexMove={onPolygonVertexMove}
            onPolygonVertexRemove={onPolygonVertexRemove}
            onPolygonDoubleClick={onEditPolygon}
            avoidPolygons={form.avoidPolygons}
          />
          <button
            className="map-float-btn"
            onClick={() => setFitBoundsSignal(x => x + 1)}
            title="Fit map to current route / waypoints"
          >🔲 Fit</button>
          {drawPolygonMode && (
            <div className="polygon-toolbar">
              <span>{editPolygonId ? '✏️ Editing polygon' : '🔺 Drawing new polygon'} — {polygonVertices.length} pts · double-click to add · drag to move · right-click to remove</span>
              <button onClick={onPolygonVertexUndo} disabled={polygonVertices.length === 0}>↩ Undo</button>
              <button onClick={onPolygonFinish} disabled={polygonVertices.length < 3} className="primary">✓ {editPolygonId ? 'Save' : 'Finish'}</button>
              <button onClick={onPolygonCancel}>✕ Cancel</button>
            </div>
          )}
          <LoadingOverlay active={sending} verb={verb} />
          {form.mode === 'directions' && routesResponse && (
            <RoutesPanel
              response={routesResponse}
              elapsedMs={elapsedMs}
              selectedIdx={selectedRouteIdx}
              visibleRoutes={visibleRoutes}
              onSelect={setSelectedRouteIdx}
              onToggleVisible={toggleRouteVisible}
            />
          )}
          {form.mode === 'matrix' && matrixResponse && matrixBody && (
            <MatrixTable response={matrixResponse} body={matrixBody} elapsedMs={elapsedMs} />
          )}
        </section>

        {/* RIGHT resize handle */}
        <div className="resize-handle" onMouseDown={(e) => startResize('right', e)} />

        {/* RIGHT: history + raw JSON */}
        <aside className="panel side-panel">
          <History
            entries={history}
            activeIdx={activeHistoryIdx}
            currentBaseUrl={form.baseUrl}
            onLoad={onHistoryLoad}
            onReplayCurrent={onHistoryReplayCurrent}
            onReplaySaved={onHistoryReplaySaved}
            onCopyCurl={copyCurlForEntry}
            onDelete={onHistoryDelete}
            onClear={onHistoryClear}
          />
          <details className="accordion" open>
            <summary>
              <span>Raw Response</span>
              <div style={{ display: 'flex', gap: 3, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                {(['json', 'geojson', 'gpx'] as const).map(tab => {
                  const available = tab === 'json' || (form.fetchAllFormats && form.mode === 'directions');
                  const loading = tab === 'geojson' ? (form.fetchAllFormats && lastResponseGeoJson === null && lastResponse !== null && lastResponse !== 'Loading…')
                    : tab === 'gpx' ? (form.fetchAllFormats && lastResponseGpx === null && lastResponse !== null && lastResponse !== 'Loading…')
                    : false;
                  return (
                    <button
                      key={tab}
                      className={`tiny${responseTab === tab ? ' active' : ''}`}
                      disabled={!available}
                      onClick={() => setResponseTab(tab)}
                      title={!available ? 'Enable "GeoJSON & GPX" checkbox to fetch this format' : tab.toUpperCase()}
                      style={responseTab === tab ? { fontWeight: 700, outline: '2px solid var(--accent, #4a9eff)' } : {}}
                    >
                      {tab.toUpperCase()}{loading ? ' ⏳' : ''}
                    </button>
                  );
                })}
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    className="tiny"
                    onClick={() => copyResponse(responseTab)}
                    onContextMenu={(e) => { e.preventDefault(); setCopyMenuOpen(v => !v); }}
                    title="Click to copy active tab · Right-click for options"
                  >copy</button>
                  {copyMenuOpen && (
                    <div
                      style={{
                        position: 'absolute', top: '110%', right: 0, zIndex: 200,
                        background: 'var(--bg2, #2a2a2a)', border: '1px solid var(--border, #444)',
                        borderRadius: 6, padding: '4px 0', minWidth: 150, boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      {(['json', 'geojson', 'gpx'] as const).map(tab => {
                        const available = tab === 'json' || (form.fetchAllFormats && form.mode === 'directions');
                        return (
                          <div
                            key={tab}
                            onClick={() => { if (available) { copyResponse(tab); setCopyMenuOpen(false); } }}
                            style={{
                              padding: '6px 14px', cursor: available ? 'pointer' : 'default',
                              opacity: available ? 1 : 0.4, fontSize: '0.82em',
                            }}
                            onMouseEnter={e => { if (available) (e.currentTarget as HTMLElement).style.background = 'var(--hover, #3a3a3a)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
                          >
                            Copy {tab.toUpperCase()}
                          </div>
                        );
                      })}
                      <div style={{ borderTop: '1px solid var(--border, #444)', margin: '4px 0' }} />
                      {(() => {
                        const available = form.fetchAllFormats && form.mode === 'directions' && lastResponse != null && lastResponseGeoJson != null;
                        return (
                          <div
                            onClick={() => { if (available) { copyJsonAndGeoJson(); setCopyMenuOpen(false); } }}
                            style={{
                              padding: '6px 14px', cursor: available ? 'pointer' : 'default',
                              opacity: available ? 1 : 0.4, fontSize: '0.82em',
                            }}
                            onMouseEnter={e => { if (available) (e.currentTarget as HTMLElement).style.background = 'var(--hover, #3a3a3a)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
                            title="Copies JSON and GeoJSON combined into one text block"
                          >
                            📋 Copy JSON + GeoJSON
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </summary>
            {(() => {
              const data = responseTab === 'geojson' ? lastResponseGeoJson
                : responseTab === 'gpx' ? lastResponseGpx
                : lastResponse;
              if (data === null || data === undefined) return <span className="muted" style={{ padding: '0.5em', display: 'block', fontSize: '0.8em' }}>—</span>;
              if (typeof data === 'string' && data !== 'Loading…')
                return <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.72em', padding: '0.5em', margin: 0, overflowX: 'auto' }}>{data}</pre>;
              return <JsonViewer data={data} />;
            })()}
          </details>
          <details className="accordion">
            <summary>
              <span>Request Body</span>
              <button
                className="tiny"
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  navigator.clipboard.writeText(
                    typeof lastRequest === 'string' ? lastRequest : JSON.stringify(lastRequest, null, 2)
                  );
                  flash('Request body copied');
                }}
              >copy</button>
            </summary>
            <JsonViewer data={lastRequest} />
          </details>
        </aside>
      </main>
    </div>
  );
}



